<template>
  <q-scroll-area style="height: calc(100vh - 50px) !important">
    <q-page fluid>
      <div class="row full-width">
        <div class="col smallStatCard q-pa-md">
          <q-card class="full-width full-height statsCard">
            <q-card-section class="full-height">
              <q-icon class="helpToolTipIcon" name="mdi-help-circle-outline">
                <q-tooltip>
                  <div class="statsCard-tooltipTextContainer">
                    This is the number of devices that have failed their
                    heartbeat checking with the API.
                  </div>
                </q-tooltip>
              </q-icon>
              <div class="container">
                <q-spinner-tail
                  v-if="devicesFailedHBLoading && !devicesFailedHB"
                  color="primary"
                  size="50px"
                />
                <h2
                  v-if="devicesFailedHB"
                  class="q-my-none stat"
                  :class="{
                    'text-positive': devicesFailedHB.length == 0,
                    'text-warning': devicesFailedHB.length > 0,
                    'text-negative': devicesFailedHB.length > 10,
                  }"
                >
                  {{ devicesFailedHB.length }}
                </h2>
                <h3 class="q-my-none description">Devices Failed Heartbeat</h3>
              </div>
            </q-card-section>
          </q-card>
        </div>

        <div
          v-if="currentUserData.role == 'superAdmin'"
          class="col-12 col-sm-6 col-md-4 smallStatCard q-pa-md"
        >
          <q-card class="full-width full-height statsCard">
            <q-card-section class="full-height">
              <q-icon class="helpToolTipIcon" name="mdi-help-circle-outline">
                <q-tooltip>
                  <div class="statsCard-tooltipTextContainer">
                    This is the number of events that need to be processed by
                    our consumer. The consumer can process 100 events at a time.
                  </div>
                </q-tooltip>
              </q-icon>
              <div class="container">
                <q-spinner-tail
                  v-if="eventsToBeProcessedLoading && !eventsToBeProcessed"
                  color="primary"
                  size="50px"
                />
                <h2
                  v-if="eventsToBeProcessed"
                  class="q-my-none stat"
                  :class="{
                    'text-positive': parseInt(eventsToBeProcessed.count) <= 100,
                    'text-warning': parseInt(eventsToBeProcessed.count) > 100,
                    'text-negative': parseInt(eventsToBeProcessed.count) > 200,
                  }"
                >
                  {{ eventsToBeProcessed.count }}
                </h2>
                <h3 class="q-my-none description">Events to be Processed</h3>
              </div>
            </q-card-section>
          </q-card>
        </div>
        <div
          v-if="currentUserData.role == 'superAdmin'"
          class="col-12 col-md-4 smallStatCard q-pa-md"
        >
          <q-card class="full-width full-height statsCard">
            <q-card-section class="full-height">
              <q-icon class="helpToolTipIcon" name="mdi-help-circle-outline">
                <q-tooltip>
                  <div class="statsCard-tooltipTextContainer">
                    This is the average amount of time it takes our consumer to
                    process a new queued event for the last hour.
                  </div>
                </q-tooltip>
              </q-icon>
              <div class="container">
                <q-spinner-tail
                  v-if="consumerAvgProcTimeLoading && !consumerAvgProcTime"
                  color="primary"
                  size="50px"
                />
                <h2
                  v-if="consumerAvgProcTime"
                  class="q-my-none stat"
                  :class="{
                    'text-positive': parseInt(consumerAvgProcTime.AvgTime) <= 6,
                    'text-warning': parseInt(consumerAvgProcTime.AvgTime) > 6,
                    'text-negative': parseInt(consumerAvgProcTime.AvgTime) > 20,
                  }"
                >
                  {{
                    Math.round(
                      (parseFloat(consumerAvgProcTime.AvgTime) +
                        Number.EPSILON) *
                        100
                    ) / 100
                  }}s
                </h2>
                <h3 class="q-my-none description">Consumer Process Time</h3>
              </div>
            </q-card-section>
          </q-card>
        </div>
        <div class="col-12 largeStatsCard q-pa-md">
          <q-card class="full-width full-height statsCard">
            <q-card-section class="full-height">
              <div class="row">
                <div class="col-12 col-sm-6">
                  <h3 class="q-my-none graphTitle">Message Frequency</h3>
                </div>
                <div class="col-12 col-sm-6">
                  <assignDevice
                    class="deviceFilter"
                    :class="{ deviceFilterMobile: $q.screen.lt.sm }"
                    clearable
                    v-model="messagesDevice_id"
                    @changed="(val) => (messagesDevice_id = val)"
                  />
                </div>
              </div>

              <messages :series="messagesSeries" />
            </q-card-section>
          </q-card>
        </div>
        <div v-if="currentUserData.hasGoogleLogin == 0" class="col-12 q-pa-md">
          <q-card>
            <q-card-section>
              <div style="display: inline-block" class="text-h6">
                Link Account with Google
              </div>
            </q-card-section>
            <q-card-section>
              <q-btn color="primary" @click="linkWithGoogle">
                Login with Google
              </q-btn>
            </q-card-section>
          </q-card>
        </div>
      </div>
    </q-page>
  </q-scroll-area>
</template>

<script>
  import { mapGetters } from "vuex";
  import messages from "@/components/statistics/messages.vue";
  import assignDevice from "@/components/devices/assignDevice.vue";

  export default {
    components: {
      messages,
      assignDevice,
    },
    computed: {
      ...mapGetters({
        currentUser: "auth/currentUser",
        currentUserData: "auth/currentUserData",
        messagesFrequency: "statistics/messagesFrequency",
        messagesFrequencyLoading: "statistics/messagesFrequencyLoading",
        devicesFailedHB: "statistics/devicesFailedHB",
        devicesFailedHBLoading: "statistics/devicesFailedHBLoading",
        consumerAvgProcTime: "statistics/consumerAvgProcTime",
        consumerAvgProcTimeLoading: "statistics/consumerAvgProcTimeLoading",
        eventsToBeProcessed: "statistics/eventsToBeProcessed",
        eventsToBeProcessedLoading: "statistics/eventsToBeProcessedLoading",
      }),
    },
    watch: {
      messagesFrequency: function () {
        this.calculateMessagesSeries();
      },
      messagesDevice_id: function () {
        this.$store.dispatch(
          "statistics/loadMessagesFrequency",
          this.messagesDevice_id
        );
      },
    },
    data: () => ({
      messagesSeries: null,
      polling: null,

      messagesDevice_id: null,
    }),
    created() {
      this.$store.dispatch("devices/loadAll");
      this.pollData();
    },
    mounted() {
      this.calculateMessagesSeries();
    },
    beforeDestroy() {
      clearInterval(this.polling);
    },
    methods: {
      linkWithGoogle() {
        this.$store.dispatch("auth/linkWithGoogle");
      },

      loadData() {
        this.$store.dispatch(
          "statistics/loadMessagesFrequency",
          this.messagesDevice_id
        );
        this.$store.dispatch("statistics/loadDevicesFailedHB");
        this.$store.dispatch("statistics/loadConsumerAvgProcTime");
        this.$store.dispatch("statistics/loadEventsToBeProcessed");
      },

      pollData() {
        this.loadData();
        this.polling = setInterval(() => {
          this.loadData();
        }, 10000);
      },

      calculateMessagesSeries() {
        if (this.messagesFrequency) {
          let localTime = this.$moment().format("YYYY-MM-DD");

          let totalMessagesSeries = [];
          let eventsSeries = [];
          this.messagesFrequency.forEach((val) => {
            let hour = this.$moment
              .utc(`${localTime} ${val.hour < 10 ? "0" : ""}${val.hour}:00:00`)
              .local()
              .format("HH:mm");

            totalMessagesSeries.push({
              x: hour,
              y: val.events,
            });

            eventsSeries.push({
              x: hour,
              y: parseInt(val.rbOrTriggerEvents),
            });
          });
          this.messagesSeries = [
            {
              name: "Total",
              type: "column",
              data: totalMessagesSeries,
            },
            {
              name: "Total Events",
              type: "column",
              data: eventsSeries,
            },
          ];
        }
      },
    },
  };
</script>

<style lang="scss">
  .statsCard-tooltipTextContainer {
    max-width: 150px !important;
    width: 150px !important;
  }

  .smallStatCard {
    min-height: 200px;
    max-height: 200px;
    height: 200px;

    .statsCard {
      position: relative;

      .helpToolTipIcon {
        font-size: 25px;
        position: absolute;
        top: 15px;
        right: 15px;
      }

      .container {
        text-align: center;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);

        .stat {
          font-size: 50px;
          font-weight: 500;
          line-height: 50px;
        }

        .description {
          line-height: 25px;
          font-size: 20px;
        }
      }
    }
  }

  .largeStatsCard {
    min-height: 400px;

    .statsCard {
      .graphTitle {
        font-size: 30px;
        font-weight: 500;
      }

      .deviceFilter {
        width: 200px;
        float: right;
      }
      .deviceFilterMobile {
        width: 100% !important;
      }
    }
  }
</style>
